import { motion } from "framer-motion";
import { useViewport } from "src/contexts";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";
import { LoadingBone, Radio } from "../atoms";
import { IconTooltip } from "./IconTooltip";
import { cardVariants, CommonOptionCardProps, TitleActionText } from "./cards";

// FIXME: This is interim value to satisfy views < 1280 until we've gotten tablet responsiveness in
const REDUCE_TITLE_SIZE_BREAKPOINT = 270;

type SelectableOptionCardProps = {
  selected?: boolean;
  onClick?: () => void;
  priceLabel?: string;
  disabled?: boolean;
  /** To trigger state from paired map marker interaction */
  isHovered?: boolean;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
} & CommonOptionCardProps;

export function SelectableOptionCard(props: SelectableOptionCardProps) {
  const {
    title,
    selected,
    description,
    onClick,
    titleAction,
    infoTooltip,
    priceLabel,
    disabled = false,
    contrast = false,
    isHovered = false,
    onHoverStart,
    onHoverEnd,
  } = props;
  const { isDesktop } = useViewport();
  const tid = useTestIds(props, "selectable-option-card");

  return (
    <motion.div
      role="button"
      data-selected={!!selected}
      id="selectable-option-card"
      variants={cardVariants}
      css={{
        ...Css.p2.w100.gap1.br4.df.fdc.cursorPointer.add("transition", "0.2s").$,
        // If disabled, we want to show the radio button but not allow interaction
        ...Css.if(disabled).cursorNotAllowed.bgGray200.bcGray400.gray400.if(disabled && !selected).o25.$,
        ...Css.if(isDesktop && !disabled).onHover.bgGray4.$,
        // Keeping this logic out of the above if else onHover logic
        ...Css.if(isDesktop && !disabled && isHovered).bgGray4.$,
      }}
      onClick={() => {
        if (!disabled) {
          onClick?.();
        }
      }}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      {...tid}
    >
      {/* Setting a container here to use with the title for a smaller font */}
      <div css={Css.df.fdc.gap1.container().$}>
        {/* Main content area with title/description on left and radio on right */}
        <div css={Css.df.jcsb.aifs.gapPx(8).$}>
          {/* Content container with title and description */}
          <div css={Css.df.fdc.gap1.$}>
            {/* Header Area */}
            {title && (
              <div css={Css.df.jcsb.relative.top0.$}>
                <div css={Css.df.gap1.aic.$}>
                  <div
                    css={{
                      ...Css.gap1.t18.gray800.$,
                      ...Css.if(contrast).white.$,
                      ...Css.ifContainer({ lt: REDUCE_TITLE_SIZE_BREAKPOINT }).fsPx(14).fw500.lhPx(22).$,
                    }}
                  >
                    {title}
                  </div>
                  {titleAction && (
                    <TitleActionText
                      contrast={contrast}
                      triggerText={titleAction.triggerText}
                      onClick={titleAction.onClick}
                      size={titleAction.size}
                      {...tid.titleActionText}
                    />
                  )}
                  {infoTooltip && <IconTooltip message={infoTooltip} type="info" {...tid.infoTooltip} />}
                </div>
                <div css={Css.df.gapPx(12).asc.$}>
                  {priceLabel && (
                    <span
                      css={{
                        ...Css.t16.gray700.wsnw.$,
                        ...Css.ifContainer({ lt: REDUCE_TITLE_SIZE_BREAKPOINT }).t14.$,
                      }}
                    >
                      {priceLabel}
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* Main body */}
            {description}
          </div>

          {/* Radio button positioned on the right with 8px gap */}
          <Radio containerBreakpoint={REDUCE_TITLE_SIZE_BREAKPOINT} selected={!!selected} disabled={disabled} />
        </div>
      </div>
    </motion.div>
  );
}

export function SkeletonSelectableOptionCard() {
  return (
    <motion.div variants={cardVariants} css={Css.p2.w100.gap1.br4.df.fdc.$}>
      {/* Setting a container here to use with the title for a smaller font */}
      <div css={Css.df.fdc.gap1.container().$}>
        {/* Main content area with title/description on left and radio on right */}
        <div css={Css.df.jcsb.aifs.gapPx(8).$}>
          {/* Content container with title and description */}
          <div css={Css.df.fdc.gap1.$}>
            {/* Header Area */}
            <div css={Css.df.jcsb.top0.$}>
              <div css={Css.df.gap1.aic.$}>
                <div css={Css.oh.gap1.t18.gray800.$}>
                  <LoadingBone width="120px" height="18px" />
                </div>
              </div>
            </div>
            {/* Main body */}
            <div css={Css.df.fdc.gap1.t14.gray80.$}>
              <LoadingBone width="150px" />
              <div css={Css.df.gap1.$}>
                <LoadingBone width="75px" />
                <LoadingBone width="75px" />
                <LoadingBone width="75px" />
              </div>
            </div>
          </div>
          <Radio selected={false} disabled />
        </div>
      </div>
    </motion.div>
  );
}
